// =============================================================================
// PAYMENT SERVICE
// =============================================================================
// Service for handling payment operations with Firebase Cloud Functions

import { httpsCallable } from 'firebase/functions';
import { functions } from './firestore';
import type { PaymentConfig } from '@/types';

export interface CreateCheckoutSessionRequest {
  paymentConfig: PaymentConfig;
  customerEmail?: string;
  customerName?: string;
  successUrl: string;
  cancelUrl: string;
}

export interface CreateCheckoutSessionResponse {
  sessionId: string;
  url: string;
  paymentConfig: PaymentConfig;
  isRecurring: boolean;
}

export interface CreatePaymentIntentRequest {
  amount: number;
  currency?: string;
  metadata?: Record<string, string>;
  paymentConfig: PaymentConfig;
}

export interface CreatePaymentIntentResponse {
  clientSecret: string;
  paymentConfig: PaymentConfig;
  paymentIntentId: string;
}

export interface CreateStripeCustomerRequest {
  userId: string;
  email: string;
  name: string;
}

export interface CreateStripeCustomerResponse {
  success: boolean;
  customerId: string;
  alreadyExists?: boolean;
}

export interface GetSubscriptionStatusRequest {
  userId: string;
}

export interface GetSubscriptionStatusResponse {
  subscribed: boolean;
  subscriptionType: string | null;
  subscriptionStatus: string | null;
  subscriptionCurrentPeriodEnd: any;
  foundingMember: boolean;
  purchasedCourses: string[];
}

class PaymentService {
  private baseUrl: string;

  constructor() {
    // Use environment variable for Firebase Functions URL
    this.baseUrl = import.meta.env.VITE_FIREBASE_FUNCTIONS;
  }

  /**
   * Create a Stripe checkout session
   */
  async createCheckoutSession(request: CreateCheckoutSessionRequest): Promise<CreateCheckoutSessionResponse> {
    try {
      // Use HTTP requests for both development and production
      const response = await fetch(`${this.baseUrl}/createCheckoutSession`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorData}`);
      }

      return await response.json();
    } catch (error) {
      // Error creating checkout session
      throw new Error(error instanceof Error ? error.message : 'Failed to create checkout session');
    }
  }

  /**
   * Create a payment intent
   */
  async createPaymentIntent(request: CreatePaymentIntentRequest): Promise<CreatePaymentIntentResponse> {
    try {
      // Use HTTP requests for both development and production
      const response = await fetch(`${this.baseUrl}/createPaymentIntent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorData}`);
      }

      return await response.json();
    } catch (error) {
      // Error creating payment intent
      throw new Error(error instanceof Error ? error.message : 'Failed to create payment intent');
    }
  }

  /**
   * Create a Stripe customer
   */
  async createStripeCustomer(request: CreateStripeCustomerRequest): Promise<CreateStripeCustomerResponse> {
    try {
      if (import.meta.env.DEV) {
        // Use local emulator in development
        const response = await fetch(`${this.baseUrl}/createStripeCustomer`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(request),
        });

        if (!response.ok) {
          const errorData = await response.text();
          throw new Error(`HTTP ${response.status}: ${errorData}`);
        }

        return await response.json();
      } else {
        // Use Firebase Functions in production
        const createStripeCustomer = httpsCallable(functions!, 'createStripeCustomer');
        const result = await createStripeCustomer(request);
        return result.data as CreateStripeCustomerResponse;
      }
    } catch (error) {
      // Error creating Stripe customer
      throw new Error(error instanceof Error ? error.message : 'Failed to create Stripe customer');
    }
  }

  /**
   * Get subscription status
   */
  async getSubscriptionStatus(request: GetSubscriptionStatusRequest): Promise<GetSubscriptionStatusResponse> {
    try {
      if (import.meta.env.DEV) {
        // Use local emulator in development
        const response = await fetch(`${this.baseUrl}/getSubscriptionStatus`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(request),
        });

        if (!response.ok) {
          const errorData = await response.text();
          throw new Error(`HTTP ${response.status}: ${errorData}`);
        }

        return await response.json();
      } else {
        // Use Firebase Functions in production
        const getSubscriptionStatus = httpsCallable(functions!, 'getSubscriptionStatus');
        const result = await getSubscriptionStatus(request);
        return result.data as GetSubscriptionStatusResponse;
      }
    } catch (error) {
      // Error getting subscription status
      throw new Error(error instanceof Error ? error.message : 'Failed to get subscription status');
    }
  }
}

// Export singleton instance
export const paymentService = new PaymentService();
export default paymentService;
